import { documentToReactComponents } from '@contentful/rich-text-react-renderer';
import { Document } from '@contentful/rich-text-types';
import { ContentfulBlogPostFields, ContentfulEntry, formatDate } from '@devtinker/shared';
import React, { useEffect, useState } from 'react';
import ReactMarkdown from 'react-markdown';
import { Link, useParams } from 'react-router-dom';
import remarkGfm from 'remark-gfm';
import { getEntries } from '../lib/contentful';

const normalizeBlockquoteQuotes = (markdown: string): string => {
  return markdown.replace(
    /(^>\s*)(["'“”‘’]{1,2})([^\n]*?)(["'“”‘’]{1,2})\s*$/gm,
    (_, prefix, _openQuote, quoteText, _closeQuote) => `${prefix}${quoteText.trim()}`,
  );
};

const BlogPost: React.FC = () => {
  const { slug } = useParams<{ slug: string }>();
  const [post, setPost] = useState<ContentfulEntry<ContentfulBlogPostFields> | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchPost = async () => {
      try {
        const entries = await getEntries('blogPost');
        const foundPost = entries.find((entry) => entry.fields.slug === slug);
        setPost(foundPost as ContentfulEntry<ContentfulBlogPostFields> || null);
      } catch (error) {
        console.error('Error fetching blog post:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchPost();
  }, [slug]);

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-green-600 mx-auto mb-4"></div>
          <p className="text-gray-600">Loading article...</p>
        </div>
      </div>
    );
  }

  if (!post) {
    return (
      <div className="max-w-4xl mx-auto text-center py-16">
        <h1 className="text-4xl font-bold text-gray-900 mb-4">Article Not Found</h1>
        <p className="text-gray-600 mb-8">
          The article you're looking for doesn't exist or has been moved.
        </p>
        <Link
          to="/blog"
          className="inline-flex items-center text-green-600 hover:text-green-700 font-medium"
        >
          <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 19l-7-7m0 0l7-7m-7 7h18" />
          </svg>
          Back to Blog
        </Link>
      </div>
    );
  }

  return (
    <article className="max-w-4xl mx-auto">
      {/* Back to Blog Link */}
      <Link
        to="/blog"
        className="inline-flex items-center text-green-600 hover:text-green-700 font-medium mb-8"
      >
        <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 19l-7-7m0 0l7-7m-7 7h18" />
        </svg>
        Back to Blog
      </Link>

      {/* Banner/Hero Image - prioritize bannerImage, then heroImage, then featuredImage */}
      {(post.fields.bannerImage?.[0] || post.fields.heroImage?.[0] || post.fields.featuredImage) && (
        <div className="w-full h-96 mb-8 rounded-lg overflow-hidden">
          <img
            src={
              post.fields.bannerImage?.[0]?.secure_url ||
              post.fields.heroImage?.[0]?.secure_url ||
              (post.fields.featuredImage?.fields.file.url.startsWith('//') 
                ? `https:${post.fields.featuredImage.fields.file.url}`
                : post.fields.featuredImage?.fields.file.url)
            }
            alt={post.fields.title}
            className="w-full h-full object-cover"
          />
        </div>
      )}

      {/* Tags */}
      {post.fields.tags && post.fields.tags.length > 0 && (
        <div className="flex flex-wrap gap-2 mb-6">
          {post.fields.tags.map((tag, index) => (
            <span
              key={index}
              className="text-xs font-semibold uppercase tracking-wide text-green-600 bg-green-50 px-3 py-1 rounded-full"
            >
              {tag}
            </span>
          ))}
        </div>
      )}

      {/* Title */}
      <h1 className="text-5xl font-bold text-gray-900 mb-6 leading-tight">
        {post.fields.title}
      </h1>

      {/* Meta Information */}
      <div className="flex items-center gap-4 text-gray-600 mb-8 pb-8 border-b border-gray-200">
        <time dateTime={post.fields.publishDate} className="text-sm">
          {formatDate(post.fields.publishDate)}
        </time>
        {post.fields.author && (
          <>
            <span>•</span>
            <span className="text-sm">by {post.fields.author}</span>
          </>
        )}
      </div>

      {/* Excerpt or Description */}
      {(post.fields.excerpt || post.fields.description) && (
        <div className="text-xl text-gray-700 leading-relaxed mb-8 p-6 bg-gray-50 rounded-lg border-l-4 border-green-600">
          {post.fields.excerpt || post.fields.description}
        </div>
      )}

      {/* Content */}
      <div className="prose prose-lg max-w-none">
        {(() => {
          // Check for both 'body' (actual Contentful field) and 'content' (import file field)
          const contentData = post.fields.body || post.fields.content;
          
          if (!contentData) {
            return (
              <div className="space-y-6 text-gray-700 leading-relaxed">
                <p>
                  This is a placeholder for blog post content. When you connect to Contentful,
                  the actual blog post content will be displayed here with rich text formatting,
                  images, and all the content you've authored.
                </p>
                <p>
                  The content will support various formatting options including:
                </p>
                <ul className="list-disc list-inside space-y-2 ml-4">
                  <li>Headers and subheaders</li>
                  <li>Paragraphs with rich text formatting</li>
                  <li>Images and embedded media</li>
                  <li>Code blocks and syntax highlighting</li>
                  <li>Quotes and callouts</li>
                  <li>Lists and tables</li>
                </ul>
                <p>
                  To get started, configure your Contentful space and create blog posts
                  using the 'blogPost' content type as described in the documentation.
                </p>
              </div>
            );
          }
          
          // If it's a string, render as Markdown
          if (typeof contentData === 'string') {
            const normalizedMarkdown = normalizeBlockquoteQuotes(contentData);

            return (
              <ReactMarkdown
                remarkPlugins={[remarkGfm]}
                components={{
                  // Fix protocol-relative URLs for images
                  img: ({ node, ...props }) => (
                    <img 
                      {...props} 
                      src={props.src?.startsWith('//') ? `https:${props.src}` : props.src}
                      loading="lazy"
                      className="rounded-lg"
                    />
                  ),
                  // Style links with prose compatibility
                  a: ({ node, ...props }) => (
                    <a {...props} className="text-green-600 hover:text-green-700" />
                  ),
                  // Ensure headings have proper spacing
                  h1: ({ node, ...props }) => (
                    <h1 {...props} className="text-4xl font-bold mt-8 mb-4 text-gray-900" />
                  ),
                  h2: ({ node, ...props }) => (
                    <h2 {...props} className="text-3xl font-bold mt-8 mb-4 text-gray-900" />
                  ),
                  h3: ({ node, ...props }) => (
                    <h3 {...props} className="text-2xl font-bold mt-6 mb-3 text-gray-900" />
                  ),
                  h4: ({ node, ...props }) => (
                    <h4 {...props} className="text-xl font-bold mt-6 mb-3 text-gray-900" />
                  ),
                  h5: ({ node, ...props }) => (
                    <h5 {...props} className="text-lg font-bold mt-4 mb-2 text-gray-900" />
                  ),
                  h6: ({ node, ...props }) => (
                    <h6 {...props} className="text-base font-bold mt-4 mb-2 text-gray-900" />
                  ),
                  // Style paragraphs
                  p: ({ node, ...props }) => (
                    <p {...props} className="mb-4 leading-relaxed text-gray-700" />
                  ),
                  // Style lists
                  ul: ({ node, ...props }) => (
                    <ul {...props} className="list-disc list-inside mb-4 space-y-2 text-gray-700" />
                  ),
                  ol: ({ node, ...props }) => (
                    <ol {...props} className="list-decimal list-inside mb-4 space-y-2 text-gray-700" />
                  ),
                  li: ({ node, ...props }) => (
                    <li {...props} className="ml-4" />
                  ),
                  // Style blockquotes
                  blockquote: ({ node, ...props }) => (
                    <blockquote {...props} className="border-l-4 border-green-600 pl-4 italic my-4 text-gray-600" />
                  ),
                  // Style tables
                  table: ({ node, ...props }) => (
                    <div className="my-6 w-full overflow-x-auto">
                      <table {...props} className="min-w-[640px] w-full border-collapse border border-gray-300" />
                    </div>
                  ),
                  thead: ({ node, ...props }) => (
                    <thead {...props} className="bg-gray-50" />
                  ),
                  tbody: ({ node, ...props }) => (
                    <tbody {...props} className="bg-white" />
                  ),
                  tr: ({ node, ...props }) => (
                    <tr {...props} className="border-b border-gray-200" />
                  ),
                  th: ({ node, ...props }) => (
                    <th
                      {...props}
                      className="border border-gray-300 px-4 py-2 text-left text-sm font-semibold text-gray-900"
                    />
                  ),
                  td: ({ node, ...props }) => (
                    <td {...props} className="border border-gray-300 px-4 py-2 text-sm text-gray-700" />
                  ),
                  // Style code blocks
                  code: ({ node, inline, ...props }: any) => 
                    inline ? (
                      <code {...props} className="bg-gray-100 text-pink-600 px-2 py-1 rounded text-sm font-mono" />
                    ) : (
                      <code {...props} className="block bg-gray-900 text-gray-100 p-4 rounded-lg overflow-x-auto my-4 text-sm font-mono" />
                    ),
                  pre: ({ node, ...props }) => (
                    <pre {...props} className="my-4" />
                  ),
                }}
              >
                {normalizedMarkdown}
              </ReactMarkdown>
            );
          }
          
          // If it's a rich text document, use the renderer
          return documentToReactComponents(contentData as Document);
        })()}
      </div>

      {/* Share Section */}
      <div className="mt-12 pt-8 border-t border-gray-200">
        <h3 className="text-lg font-semibold text-gray-900 mb-4">Share this article</h3>
        <div className="flex gap-4">
          {/* Twitter/X Share */}
          <button 
            onClick={() => {
              const url = encodeURIComponent(window.location.href);
              const text = encodeURIComponent(post.fields.title);
              window.open(`https://twitter.com/intent/tweet?url=${url}&text=${text}`, '_blank', 'width=550,height=420');
            }}
            className="p-2 rounded-lg border border-gray-300 hover:border-green-600 hover:text-green-600 transition-colors"
            aria-label="Share on Twitter"
          >
            <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
              <path d="M8.29 20.251c7.547 0 11.675-6.253 11.675-11.675 0-.178 0-.355-.012-.53A8.348 8.348 0 0022 5.92a8.19 8.19 0 01-2.357.646 4.118 4.118 0 001.804-2.27 8.224 8.224 0 01-2.605.996 4.107 4.107 0 00-6.993 3.743 11.65 11.65 0 01-8.457-4.287 4.106 4.106 0 001.27 5.477A4.072 4.072 0 012.8 9.713v.052a4.105 4.105 0 003.292 4.022 4.095 4.095 0 01-1.853.07 4.108 4.108 0 003.834 2.85A8.233 8.233 0 012 18.407a11.616 11.616 0 006.29 1.84" />
            </svg>
          </button>
          
          {/* LinkedIn Share */}
          <button 
            onClick={() => {
              const url = encodeURIComponent(window.location.href);
              window.open(`https://www.linkedin.com/sharing/share-offsite/?url=${url}`, '_blank', 'width=550,height=420');
            }}
            className="p-2 rounded-lg border border-gray-300 hover:border-green-600 hover:text-green-600 transition-colors"
            aria-label="Share on LinkedIn"
          >
            <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
              <path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433c-1.144 0-2.063-.926-2.063-2.065 0-1.138.92-2.063 2.063-2.063 1.14 0 2.064.925 2.064 2.063 0 1.139-.925 2.065-2.064 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z"/>
            </svg>
          </button>
          
          {/* Copy Link */}
          <button 
            onClick={async () => {
              try {
                await navigator.clipboard.writeText(window.location.href);
                alert('Link copied to clipboard!');
              } catch (err) {
                console.error('Failed to copy link:', err);
              }
            }}
            className="p-2 rounded-lg border border-gray-300 hover:border-green-600 hover:text-green-600 transition-colors"
            aria-label="Copy link to clipboard"
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 5H6a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2v-1M8 5a2 2 0 002 2h2a2 2 0 002-2M8 5a2 2 0 012-2h2a2 2 0 012 2m0 0h2a2 2 0 012 2v3m2 4H10m0 0l3-3m-3 3l3 3" />
            </svg>
          </button>
        </div>
      </div>

      {/* Related Posts Section */}
      <div className="mt-16 pt-12 border-t border-gray-200">
        <h3 className="text-2xl font-bold text-gray-900 mb-8">More Articles</h3>
        <div className="grid md:grid-cols-2 gap-6">
          <Link to="/blog" className="group">
            <div className="bg-white border border-gray-200 rounded-lg p-6 hover:shadow-lg transition-shadow">
              <h4 className="text-lg font-semibold text-gray-900 group-hover:text-green-600 transition-colors mb-2">
                Explore more articles
              </h4>
              <p className="text-gray-600 text-sm">
                Browse our complete collection of articles and guides
              </p>
            </div>
          </Link>
        </div>
      </div>
    </article>
  );
};

export default BlogPost;
